import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { Book, Download, Upload, Plus, Trash2, Check, X, ChevronDown, ChevronRight, Package, Sparkles, Shield, Award, Star, Users, Edit2, Trash } from 'lucide-react';
import { sourcesManager } from '../data/sourcesManager';
import { useUI } from '../contexts/UIContext';
import { systemBus, EVENTS } from '../services/systemBus';
import SourceContentDialog from './SourceContentDialog';
import MobileModal from './MobileModal';

const SourcesPanel = () => {
  // [PHASE 4B - MIGRATED]: Now using context hooks instead of props
  const { showStatus, showDialog } = useUI();
  const [sources, setSources] = useState({});
  const [expandedSource, setExpandedSource] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // [v1.0]: Added races, classes, subclasses categories for full content import
  const [selectedCategories, setSelectedCategories] = useState({
    items: true,
    spells: true,
    feats: false,
    backgrounds: false,
    abilities: false,
    boons: false,
    races: true,
    classes: true,
    subclasses: true
  });
  const [newSourceData, setNewSourceData] = useState({
    name: '',
    author: '',
    description: ''
  });
  const [importFile, setImportFile] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [activeTab, setActiveTab] = useState('items'); // items, spells, feats
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [contentDialogData, setContentDialogData] = useState(null);
  // [FIX NOV 22, 2025]: Add state for edit source dialog
  const [showEditSourceDialog, setShowEditSourceDialog] = useState(false);
  const [editSourceData, setEditSourceData] = useState({
    id: '',
    name: '',
    author: '',
    version: '',
    description: ''
  });

  // [UI-MOBILEMODAL-ADOPTION]: These onClose resets now feed MobileModal directly
  // (it self-registers useModalEscape), so Escape and the X/backdrop-adjacent Cancel
  // path always run the SAME reset.
  const closeImportDialog = useCallback(() => {
    setShowImportDialog(false);
    setImportFile(null);
  }, []);

  const closeCreateDialog = useCallback(() => {
    setShowCreateDialog(false);
    setNewSourceData({ name: '', author: '', description: '' });
  }, []);

  // [UI-MOBILEMODAL-ADOPTION]: previously only setShowEditSourceDialog(false) — the
  // Cancel button also reset editSourceData but Escape didn't. MobileModal routes both
  // through this single onClose, so that asymmetry is gone.
  const closeEditSourceDialog = useCallback(() => {
    setShowEditSourceDialog(false);
    setEditSourceData({ id: '', name: '', author: '', version: '', description: '' });
  }, []);

  // [PHASE 5 SESSION 3]: Force re-render mechanism for reactive sources panel
  // [PURPOSE]: Enables panel to react to source enable/disable events from other locations
  // [WHY]: When sources are toggled from settings, external scripts, or party sync, panel needs to refresh
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 5 SESSION 3]: Event subscriptions for reactive sources panel
  // [PURPOSE]: Subscribe to source enable/disable events for real-time content availability updates
  // [WHY]: This panel needs to show real-time updates when:
  //   - Sources are enabled/disabled from settings or external sources
  //   - Party members enable/disable shared content sources
  //   - DM pushes source changes to party
  //   - Content is imported/exported programmatically
  // [INTEGRATION]: Ensures source list and statistics stay synchronized across all interfaces
  useEffect(() => {
    const handleSourceEvent = (eventName, data) => {
      // Reload sources and update statistics when source state changes
      loadSources();
      updateStatistics();
      forceUpdate();
    };

    // [SOURCE_ENABLED]: Content source was enabled (adds items/spells/feats)
    const unsubscribeEnabled = systemBus.on(EVENTS.SOURCE_ENABLED, (data) =>
      handleSourceEvent('SOURCE_ENABLED', data));

    // [SOURCE_DISABLED]: Content source was disabled (removes items/spells/feats)
    const unsubscribeDisabled = systemBus.on(EVENTS.SOURCE_DISABLED, (data) =>
      handleSourceEvent('SOURCE_DISABLED', data));

    return () => {
      unsubscribeEnabled();
      unsubscribeDisabled();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load sources on component mount
  useEffect(() => {
    loadSources();
    updateStatistics();
  }, []);

  const loadSources = () => {
    setSources(sourcesManager.getAllSources());
  };

  const updateStatistics = () => {
    setStatistics(sourcesManager.getStatistics());
  };

  const handleToggleSource = (sourceId) => {
    const source = sources[sourceId];
    const newState = !source.enabled;

    // [PHASE 6A]: Store old state for event emission
    const oldState = source.enabled;

    sourcesManager.toggleSource(sourceId, newState);

    // [PHASE 6A]: Emit SOURCE_ENABLED or SOURCE_DISABLED event based on new state
    if (newState) {
      systemBus.emit(EVENTS.SOURCE_ENABLED, {
        sourceId,
        source: {
          id: sourceId,
          name: source.name,
          author: source.author,
          description: source.description,
          contentTypes: source.content ? Object.keys(source.content) : [],
          itemCount: source.content?.items?.length || 0,
          spellCount: source.content?.spells?.length || 0,
          featCount: source.content?.feats?.length || 0
        },
        previousState: oldState
      });
    } else {
      systemBus.emit(EVENTS.SOURCE_DISABLED, {
        sourceId,
        source: {
          id: sourceId,
          name: source.name,
          author: source.author,
          description: source.description
        },
        previousState: oldState
      });
    }

    loadSources();
    updateStatistics();
    showStatus(`${source.name} ${newState ? 'enabled' : 'disabled'}`);
  };

  const handleRemoveSource = (sourceId) => {
    const source = sources[sourceId];
    if (source.removable) {
      showDialog({
        title: 'Remove Source',
        content: `Are you sure you want to remove "${source.name}"? This action cannot be undone.`,
        buttons: [
          {
            text: 'Cancel',
            onClick: () => {}
          },
          {
            text: 'Remove',
            onClick: () => {
              if (sourcesManager.removeSource(sourceId)) {
                loadSources();
                updateStatistics();
                showStatus(`${source.name} removed`);
              }
            },
            className: 'bg-red-600 hover:bg-red-700 text-white'
          }
        ]
      });
    }
  };

  const handleExportSource = (sourceId) => {
    try {
      const jsonData = sourcesManager.exportSource(sourceId);
      const source = sources[sourceId];
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${source.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}_source.json`;
      a.click();
      URL.revokeObjectURL(url);
      showStatus(`${source.name} exported successfully`);
    } catch (e) {
      showStatus(`Failed to export source: ${e.message}`, 'error');
    }
  };

  const handleExportAll = () => {
    try {
      const sourceIds = Object.keys(sources).filter(id => sources[id].enabled);
      const jsonData = sourcesManager.exportSources(sourceIds);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_sources.json';
      a.click();
      URL.revokeObjectURL(url);
      showStatus('All enabled sources exported successfully');
    } catch (e) {
      showStatus(`Failed to export sources: ${e.message}`, 'error');
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImportFile(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleImportSource = () => {
    if (!importFile) {
      showStatus('Please select a file to import', 'error');
      return;
    }

    try {
      const sourceData = JSON.parse(importFile);

      // Check if it's multiple sources or single source
      if (sourceData.sources) {
        // Multiple sources
        for (const source of Object.values(sourceData.sources)) {
          sourcesManager.importSource(source, selectedCategories);
        }
        showStatus('Sources imported successfully');
      } else {
        // Single source
        sourcesManager.importSource(sourceData, selectedCategories);
        showStatus('Source imported successfully');
      }

      loadSources();
      updateStatistics();
      setShowImportDialog(false);
      setImportFile(null);
    } catch (e) {
      showStatus(`Failed to import source: ${e.message}`, 'error');
    }
  };

  const handleCreateSource = () => {
    if (!newSourceData.name) {
      showStatus('Please enter a source name', 'error');
      return;
    }

    try {
      sourcesManager.createSource(
        newSourceData.name,
        newSourceData.author || 'Custom',
        newSourceData.description
      );
      showStatus(`${newSourceData.name} created successfully`);
      loadSources();
      updateStatistics();
      setShowCreateDialog(false);
      setNewSourceData({ name: '', author: '', description: '' });
    } catch (e) {
      showStatus(`Failed to create source: ${e.message}`, 'error');
    }
  };

  // [FIX NOV 22, 2025]: Handle editing source metadata
  const handleEditSourceMetadata = (sourceId) => {
    const source = sources[sourceId];
    if (!source) return;

    setEditSourceData({
      id: sourceId,
      name: source.name,
      author: source.author,
      version: source.version,
      description: source.description
    });
    setShowEditSourceDialog(true);
  };

  // [FIX NOV 22, 2025]: Save edited source metadata
  const handleSaveEditedSource = () => {
    if (!editSourceData.name) {
      showStatus('Please enter a source name', 'error');
      return;
    }

    try {
      sourcesManager.updateSource(editSourceData.id, {
        name: editSourceData.name,
        author: editSourceData.author,
        version: editSourceData.version,
        description: editSourceData.description
      });
      showStatus(`${editSourceData.name} updated successfully`);
      loadSources();
      setShowEditSourceDialog(false);
      setEditSourceData({ id: '', name: '', author: '', version: '', description: '' });
    } catch (e) {
      showStatus(`Failed to update source: ${e.message}`, 'error');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      items: <Package className="w-4 h-4" />,
      spells: <Sparkles className="w-4 h-4" />,
      feats: <Star className="w-4 h-4" />,
      backgrounds: <Users className="w-4 h-4" />,
      subclasses: <Shield className="w-4 h-4" />,
      races: <Users className="w-4 h-4" />, // [CONTENT CREATORS - FEB 2026]
      classes: <Award className="w-4 h-4" />, // [CONTENT CREATORS - FEB 2026]
      abilities: <Award className="w-4 h-4" />,
      boons: <Shield className="w-4 h-4" />
    };
    return icons[category] || <Package className="w-4 h-4" />;
  };

  const getCategoryCount = (source, category) => {
    if (source.content && source.content[category]) {
      return Object.keys(source.content[category]).length;
    }
    return 0;
  };

  const handleAddContent = (sourceId, category) => {
    setContentDialogData({
      sourceId,
      category,
      mode: 'add'
    });
    setShowContentDialog(true);
  };

  const handleEditContent = (sourceId, category, contentKey, content) => {
    setContentDialogData({
      sourceId,
      category,
      contentKey,
      content,
      mode: 'edit'
    });
    setShowContentDialog(true);
  };

  const handleDeleteContent = (sourceId, category, contentKey, contentName) => {
    showDialog({
      title: 'Delete Content',
      content: `Are you sure you want to delete "${contentName}" from this source?`,
      buttons: [
        {
          text: 'Cancel',
          onClick: () => {}
        },
        {
          text: 'Delete',
          onClick: () => {
            if (sourcesManager.removeContentFromSource(sourceId, category, contentKey)) {
              loadSources();
              updateStatistics();
              showStatus(`${contentName} deleted from source`);
            } else {
              showStatus('Failed to delete content', 'error');
            }
          },
          className: 'bg-red-600 hover:bg-red-700 text-white'
        }
      ]
    });
  };

  const handleContentSaved = () => {
    loadSources();
    updateStatistics();
    setShowContentDialog(false);
    setContentDialogData(null);
  };

  return (
    <div className="p-4 bg-gray-800 rounded-lg">
      {/* [UI-MOBILE-HEADER-OVERFLOW 2026-07-04]: flex-wrap — Create/Import/Export All
          row measured 427px of content width at a 390px viewport. */}
      <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
        <h3 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
          <Book className="w-5 h-5" />
          Content Sources
        </h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCreateDialog(true)}
            className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-white text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
          <button
            onClick={() => setShowImportDialog(true)}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm flex items-center gap-1"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={handleExportAll}
            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded text-white text-sm flex items-center gap-1"
          >
            <Download className="w-4 h-4" />
            Export All
          </button>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="mb-4 p-3 bg-gray-700 rounded-lg">
          <div className="text-sm text-gray-300 mb-2">
            {statistics.totalSources} source{statistics.totalSources !== 1 ? 's' : ''} loaded,{' '}
            {statistics.enabledSources} active
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>Items: {statistics.categories.items}</div>
            <div>Spells: {statistics.categories.spells}</div>
            <div>Feats: {statistics.categories.feats}</div>
            <div>Backgrounds: {statistics.categories.backgrounds}</div>
            <div>Abilities: {statistics.categories.abilities}</div>
            <div>Boons: {statistics.categories.boons}</div>
          </div>
        </div>
      )}

      {/* Sources List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {Object.entries(sources).map(([sourceId, source]) => (
          <div key={sourceId} className="bg-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpandedSource(expandedSource === sourceId ? null : sourceId)}
                  className="text-gray-400 hover:text-gray-200"
                >
                  {expandedSource === sourceId ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                <input
                  type="checkbox"
                  checked={source.enabled}
                  onChange={() => handleToggleSource(sourceId)}
                  className="rounded bg-gray-600 border-gray-500"
                  disabled={!source.removable && source.enabled}
                />
                <div>
                  <div className="text-sm font-medium text-gray-200">
                    {source.name}
                    {!source.removable && (
                      <span className="ml-2 text-xs text-gray-500">(Core)</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    by {source.author} • v{source.version}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {/* [FIX NOV 22, 2025]: Add edit button for editable sources */}
                {source.editable && (
                  <button
                    onClick={() => handleEditSourceMetadata(sourceId)}
                    className="p-1 text-blue-400 hover:text-blue-300"
                    title="Edit Source"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleExportSource(sourceId)}
                  className="p-1 text-gray-400 hover:text-gray-200"
                  title="Export Source"
                >
                  <Download className="w-4 h-4" />
                </button>
                {source.removable && (
                  <button
                    onClick={() => handleRemoveSource(sourceId)}
                    className="p-1 text-red-400 hover:text-red-300"
                    title="Remove Source"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {expandedSource === sourceId && (
              <div className="mt-3 pt-3 border-t border-gray-600">
                {source.description && (
                  <p className="text-xs text-gray-400 mb-3">{source.description}</p>
                )}

                {/* Content Tabs */}
                {/* [PHASE 3.1.10 - NOV 11, 2025]: Added backgrounds tab for 13 SRD backgrounds */}
                {/* [UX-12 - NOV 15, 2025]: Added abilities and boons tabs to show ALL content categories */}
                <div className="flex gap-2 mb-3 border-b border-gray-600 overflow-x-auto" data-no-swipe="true">
                  {['items', 'spells', 'feats', 'backgrounds', 'subclasses', 'races', 'classes', 'abilities', 'boons'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1 text-sm capitalize whitespace-nowrap ${
                        activeTab === tab
                          ? 'text-blue-400 border-b-2 border-blue-400'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {tab} ({getCategoryCount(source, tab)})
                    </button>
                  ))}
                </div>

                {/* Content List */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {source.content[activeTab] && Object.entries(source.content[activeTab]).length > 0 ? (
                    Object.entries(source.content[activeTab]).map(([key, content]) => (
                      <div key={key} className="flex items-center justify-between p-2 bg-gray-600 rounded">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(activeTab)}
                          <span className="text-sm text-gray-200">
                            {content.name || key.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {source.removable && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditContent(sourceId, activeTab, key, content)}
                              className="p-1 text-blue-400 hover:text-blue-300"
                              title="Edit"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteContent(sourceId, activeTab, key, content.name || key)}
                              className="p-1 text-red-400 hover:text-red-300"
                              title="Delete"
                            >
                              <Trash className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 text-sm py-4">
                      No {activeTab} in this source
                    </div>
                  )}
                </div>

                {/* Add Content Button */}
                {source.removable && (
                  <button
                    onClick={() => handleAddContent(sourceId, activeTab)}
                    className="mt-3 w-full px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-white text-sm flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add {activeTab.slice(0, -1)} to Source
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Import Dialog */}
      <MobileModal
        isOpen={showImportDialog}
        onClose={closeImportDialog}
        title="Import Source"
        onSave={handleImportSource}
        saveLabel="Import"
        maxWidthClass="max-w-md"
      >
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">Select Source File</label>
          <input
            type="file"
            accept=".json"
            onChange={handleImportFile}
            className="w-full text-sm text-gray-300"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">Import Categories</label>
          <div className="space-y-2">
            {Object.keys(selectedCategories).map(category => (
              <label key={category} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedCategories[category]}
                  onChange={(e) => setSelectedCategories({
                    ...selectedCategories,
                    [category]: e.target.checked
                  })}
                  className="rounded bg-gray-600 border-gray-500"
                />
                <span className="text-sm capitalize">{category}</span>
              </label>
            ))}
          </div>
        </div>
      </MobileModal>

      {/* Create Dialog */}
      <MobileModal
        isOpen={showCreateDialog}
        onClose={closeCreateDialog}
        title="Create New Source"
        onSave={handleCreateSource}
        saveLabel="Create"
        maxWidthClass="max-w-md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Name *</label>
            <input
              type="text"
              value={newSourceData.name}
              onChange={(e) => setNewSourceData({
                ...newSourceData,
                name: e.target.value
              })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              placeholder="My Custom Source"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Author</label>
            <input
              type="text"
              value={newSourceData.author}
              onChange={(e) => setNewSourceData({
                ...newSourceData,
                author: e.target.value
              })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              placeholder="Your Name"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              value={newSourceData.description}
              onChange={(e) => setNewSourceData({
                ...newSourceData,
                description: e.target.value
              })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              rows="3"
              placeholder="A collection of custom content..."
            />
          </div>
        </div>
      </MobileModal>

      {/* [FIX NOV 22, 2025]: Edit Source Dialog */}
      <MobileModal
        isOpen={showEditSourceDialog}
        onClose={closeEditSourceDialog}
        title="Edit Source"
        onSave={handleSaveEditedSource}
        saveLabel="Save Changes"
        maxWidthClass="max-w-md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Name *</label>
            <input
              type="text"
              value={editSourceData.name}
              onChange={(e) => setEditSourceData({
                ...editSourceData,
                name: e.target.value
              })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              placeholder="Source Name"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Author</label>
            <input
              type="text"
              value={editSourceData.author}
              onChange={(e) => setEditSourceData({
                ...editSourceData,
                author: e.target.value
              })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              placeholder="Author Name"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Version</label>
            <input
              type="text"
              value={editSourceData.version}
              onChange={(e) => setEditSourceData({
                ...editSourceData,
                version: e.target.value
              })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              placeholder="1.0"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              value={editSourceData.description}
              onChange={(e) => setEditSourceData({
                ...editSourceData,
                description: e.target.value
              })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              rows="3"
              placeholder="Source description..."
            />
          </div>
        </div>
      </MobileModal>

      {/* Content Management Dialog */}
      {showContentDialog && contentDialogData && (
        <SourceContentDialog
          data={contentDialogData}
          onSave={handleContentSaved}
          onCancel={() => {
            setShowContentDialog(false);
            setContentDialogData(null);
          }}
        />
      )}
    </div>
  );
};

export default SourcesPanel;